import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

export default function useConversationDetailsRoute() {
    const history = useHistory();
    const location = useLocation();
    const openedEntry = useRef<string>();
    const previouslyOpen = useRef<number | null>(null);
    const rawId = new URLSearchParams(location.search).get('conversation');
    const conversationId = rawId && /^\d+$/.test(rawId) ? Number(rawId) : null;

    useEffect(() => {
        const previous = previouslyOpen.current;
        previouslyOpen.current = conversationId;
        if (previous && !conversationId) {
            // Responsive layouts can replace the original trigger while the drawer is open.
            const frame = window.requestAnimationFrame(() => {
                document.getElementById(`conversation-trigger-${previous}`)?.focus({ preventScroll: true });
            });
            return () => window.cancelAnimationFrame(frame);
        }
    }, [conversationId]);

    useEffect(() => {
        if (!('scrollRestoration' in window.history)) return;
        const previous = window.history.scrollRestoration;
        window.history.scrollRestoration = 'manual';
        return () => {
            window.history.scrollRestoration = previous;
        };
    }, []);

    const open = (id: number, managerId?: string) => {
        const params = new URLSearchParams(location.search);
        params.set('conversation', String(id));
        if (managerId) params.set('managerId', managerId);
        const next = { ...location, search: `?${params.toString()}` };
        if (rawId) history.replace(next);
        else history.push(next);
        openedEntry.current = history.location.key;
    };

    const close = () => {
        if (openedEntry.current && location.key === openedEntry.current) {
            history.goBack();
        } else {
            // Closing a direct link must stay on its current page, not leave the cabinet.
            const params = new URLSearchParams(location.search);
            params.delete('conversation');
            history.replace({ ...location, search: params.toString() ? `?${params.toString()}` : '' });
        }
    };

    return {
        conversationId: Number.isSafeInteger(conversationId) && (conversationId || 0) > 0 ? conversationId : null,
        open,
        close,
    };
}
